<template>
  <div class="home">
    <div class="home-inner">
      <TournamentStrip />

      <section
        class="keep-all"
        :class="{ folded: keepAllFolded }"
        aria-labelledby="keep-all-title"
      >
        <div class="notice-head">
          <div class="notice-head-copy">
            <p class="kicker">{{ t('home.keepAllKicker') }}</p>
            <h2 id="keep-all-title">{{ t('home.keepAllTitle') }}</h2>
          </div>
          <button
            type="button"
            class="notice-toggle"
            :aria-expanded="!keepAllFolded"
            @click="keepAllToggle"
          >
            {{ keepAllFolded ? t('home.noticeExpand') : t('home.noticeCollect') }}
          </button>
        </div>
        <div v-show="!keepAllFolded" class="notice-body">
          <p v-for="key in KEEP_ALL_PARAS" :key="key">
            <template v-for="(part, i) in emphasisParts(t(key))" :key="i">
              <strong v-if="part.emphasis === 'em'" class="em">{{ part.text }}</strong>
              <strong v-else-if="part.emphasis === 'wipe'" class="wipe">{{ part.text }}</strong>
              <template v-else>{{ part.text }}</template>
            </template>
          </p>
        </div>
      </section>

      <section
        class="next-set"
        :class="{ folded: nextSetFolded }"
        aria-labelledby="next-set-title"
      >
        <img
          v-show="!nextSetFolded"
          class="next-set-art"
          src="/assets/home/spell-ruler.webp"
          :alt="t('home.nextSetArtAlt')"
        />
        <div class="next-set-copy">
          <div class="notice-head">
            <div class="notice-head-copy">
              <p class="kicker">{{ t('home.nextSetKicker') }}</p>
              <h2 id="next-set-title">{{ t('home.nextSetTitle') }}</h2>
            </div>
            <button
              type="button"
              class="notice-toggle"
              :aria-expanded="!nextSetFolded"
              @click="nextSetToggle"
            >
              {{ nextSetFolded ? t('home.noticeExpand') : t('home.noticeCollect') }}
            </button>
          </div>
          <div v-show="!nextSetFolded" class="notice-body">
            <p v-for="key in NEXT_SET_PARAS" :key="key">
              <template v-for="(part, i) in emphasisParts(t(key))" :key="i">
                <strong v-if="part.emphasis === 'em'" class="em">{{ part.text }}</strong>
                <template v-else>{{ part.text }}</template>
              </template>
            </p>
          </div>
        </div>
      </section>

      <section v-if="lastCall.open" class="last-call">
        <p>
          {{ t('home.lastCall', { current: lastCall.played, target: lastCall.packAt }) }}
        </p>
        <router-link to="/missions?tab=last-call" class="text-btn">
          {{ t('home.lastCallOpen') }}
        </router-link>
      </section>

      <ForbiddenCardsPoll />

      <header class="hero">
        <p v-if="t('home.kicker')" class="kicker">{{ t('home.kicker') }}</p>
        <h1>{{ t('home.title') }}</h1>
        <p class="session">
          {{ t('auth.signedIn', { name: player.username }) }}
        </p>
        <label class="deck-pick">
          <span class="deck-pick-label">{{ t('home.playDeck') }}</span>
          <select
            v-if="deckOptions.length > 0"
            class="deck-select"
            :value="activeDeckId"
            :aria-label="t('home.playDeck')"
            @change="onActiveDeckChange"
          >
            <option
              v-for="opt in deckOptions"
              :key="opt.id"
              :value="opt.id"
              :disabled="!opt.playable && opt.id !== activeDeckId"
            >
              {{ opt.name }}{{ opt.playable ? '' : t('home.deckNotReady') }}
            </option>
          </select>
          <span v-else class="chip">{{ t('deck.buildForty') }}</span>
        </label>
        <PwaInstallButton tone="home" />
        <div class="hero-links">
          <DiscordJoinButton />
          <SupportButton />
        </div>
      </header>

      <div class="menu">
        <div v-if="site.eventQueue" class="md-card event-feature">
          <button type="button" class="event-play" @click="playEvent">
            <span class="media">
              <img :src="ART.event" alt="" />
            </span>
            <span class="content">
              <span class="event-copy">
                <span class="label-sm">{{ t('home.eventTag') }}</span>
                <span class="title">{{ t(`eventQueue.modes.${site.eventQueue.mode}`) }}</span>
                <span class="support">{{ t(`eventQueue.modeHints.${site.eventQueue.mode}`) }}</span>
                <span class="support">{{ t('home.eventHint') }}</span>
                <span v-if="site.eventCountdown" class="support">
                  {{ t('eventQueue.endsIn', { time: site.eventCountdown }) }}
                </span>
              </span>
              <span
                v-if="ranked.eventWaiting !== null"
                class="support queue-count event-queue"
              >
                {{ t('home.queueWaiting', { n: ranked.eventWaiting }) }}
              </span>
            </span>
          </button>
          <router-link to="/rank?board=event" class="event-standings">
            {{
              t('home.eventRankHint', {
                rank: t('home.rank'),
                tab: t('rank.tabs.event'),
              })
            }}
          </router-link>
        </div>

        <button type="button" class="md-card" @click="startAIDuel">
          <span class="media">
            <img :src="ART.vsAi" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.vsAiTag') }}</span>
            <span class="title">{{ t('home.vsAi') }}</span>
            <span class="support">{{ t('home.vsAiHint') }}</span>
          </span>
        </button>

        <button type="button" class="md-card" @click="playOnline">
          <span class="media">
            <img :src="ART.online" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.onlineTag') }}</span>
            <span class="title">{{ t('home.playOnline') }}</span>
            <span class="support">
              {{ ranked.closed ? t('home.queueClosed') : t('home.onlineHint') }}
            </span>
            <span v-if="!ranked.closed && ranked.waiting !== null" class="support queue-count">
              {{ t('home.queueWaiting', { n: ranked.waiting }) }}
            </span>
            <span v-if="!ranked.closed && duelPrefs.rankedPrompts && nudgeCoins > 0 && ranked.offerPending > 0" class="support offer-line">
              {{ t('home.rankedOffer', { n: nudgeCoins }) }}
            </span>
          </span>
        </button>

        <button type="button" class="md-card" @click="playCasual">
          <span class="media">
            <img :src="ART.casual" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.casualTag') }}</span>
            <span class="title">{{ t('home.casual') }}</span>
            <span class="support">
              {{
                ranked.closed
                  ? t('home.queueClosed')
                  : t('home.casualHint')
              }}
            </span>
            <span
              v-if="!ranked.closed && ranked.casualWaiting !== null"
              class="support queue-count"
            >
              {{ t('home.queueWaiting', { n: ranked.casualWaiting }) }}
            </span>
          </span>
        </button>

        <router-link to="/rank" class="md-card">
          <span class="media media-coin media-rank">
            <span class="rank-art">
              <img :src="ART.rank" alt="" />
            </span>
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.rankTag') }}</span>
            <span class="title">{{ t('home.rank') }}</span>
            <span class="support">{{ t('home.rankHint') }}</span>
          </span>
        </router-link>

        <router-link to="/rooms" class="md-card">
          <span class="media">
            <img :src="ART.rooms" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.roomsTag') }}</span>
            <span class="title">{{ t('home.rooms') }}</span>
            <span class="support">{{ t('home.roomsHint') }}</span>
          </span>
        </router-link>

        <router-link to="/watch" class="md-card">
          <span class="media">
            <img :src="ART.watch" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.watchTag') }}</span>
            <span class="title">{{ t('home.watch') }}</span>
            <span class="support">{{ t('home.watchHint') }}</span>
          </span>
        </router-link>

        <router-link to="/missions" class="md-card">
          <span
            v-if="missions.claimableCount > 0"
            class="home-notice"
            :aria-label="t('missions.pendingBadge', { n: missions.claimableCount })"
          >{{ missions.claimableCount }}</span>
          <span class="media media-coin">
            <img :src="ART.missions" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.missionsTag') }}</span>
            <span class="title">{{ t('home.missions') }}</span>
            <span class="support">{{ t('home.missionsHint') }}</span>
          </span>
        </router-link>

        <router-link to="/packs" class="md-card">
          <span class="media">
            <img :src="ART.packs" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.packsTag') }}</span>
            <span class="title">{{ t('home.packs') }}</span>
            <span class="support">{{ t('home.packsHint') }}</span>
          </span>
        </router-link>

        <router-link to="/deck-builder" class="md-card">
          <span class="media">
            <img :src="ART.deck" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.deckTag') }}</span>
            <span class="title">{{ t('home.deckBuilder') }}</span>
            <span class="support">{{ t('home.deckHint') }}</span>
          </span>
        </router-link>

        <router-link to="/collection" class="md-card">
          <span class="media">
            <img :src="ART.collection" alt="" />
          </span>
          <span class="content">
            <span class="label-sm">{{ t('home.collectionTag') }}</span>
            <span class="title">{{ t('home.collection') }}</span>
            <span class="support">{{ t('home.collectionHint') }}</span>
          </span>
        </router-link>

        <router-link
          to="/changelog"
          class="md-card news"
          :aria-label="newsLinkLabel"
        >
          <span class="content">
            <span class="label-sm">{{ t('changelog.kicker') }}</span>
            <span class="title news-title">
              {{ t('nav.news') }}
              <span
                v-if="hasPendingNews"
                class="news-dot"
                aria-hidden="true"
              />
            </span>
            <span class="support">{{ t('home.changelogHint') }}</span>
          </span>
          <span class="trailing" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </router-link>

        <router-link to="/meta" class="md-card news">
          <span class="content">
            <span class="label-sm">{{ t('meta.kicker') }}</span>
            <span class="title">{{ t('nav.popular') }}</span>
            <span class="support">{{ t('home.popularHint') }}</span>
          </span>
          <span class="trailing" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </router-link>

        <router-link to="/banlist" class="md-card news">
          <span class="content">
            <span class="label-sm">{{ t('banlist.kicker') }}</span>
            <span class="title">{{ t('nav.banlist') }}</span>
            <span class="support">{{ t('home.banlistHint') }}</span>
          </span>
          <span class="trailing" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </router-link>

        <router-link to="/faq" class="md-card news">
          <span class="content">
            <span class="label-sm">{{ t('faq.kicker') }}</span>
            <span class="title">{{ t('nav.faq') }}</span>
            <span class="support">{{ t('home.faqHint') }}</span>
          </span>
          <span class="trailing" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </router-link>

      </div>
    </div>
    <CardsRestoredDialog
      v-if="showRestoreDialog"
      :copies="restoreCopies"
      @close="onRestoreClose"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from '../api/http'
import { useAuthStore } from '../stores/auth'
import { useCollectionStore } from '../stores/collection'
import { DuelSfx } from '../game/audio/DuelSfx'
import { activateFirstPlayable, listDeckPlayOptions } from '../game/localDeck'
import { useDeckStore } from '../stores/deck'
import { usePlayerStore } from '../stores/player'
import { useMissionsStore } from '../stores/missions'
import { useLastCallStore } from '../stores/lastCall'
import { useRankedLobbyStore } from '../stores/rankedLobby'
import { useSiteStore } from '../stores/site'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { emphasisParts } from '../i18n/emphasis'
import { useI18n } from '../i18n/t'
import { useChangelogNotice } from '../composables/useChangelogNotice'
import { useHomeNoticeFold } from '../composables/useHomeNoticeFold'
import { armDuelLandscape } from '../composables/useDuelOrientation'
import { RANKED_NUDGE_COINS } from '@shared/index'
import CardsRestoredDialog from '../components/CardsRestoredDialog.vue'
import ForbiddenCardsPoll from '../components/ForbiddenCardsPoll.vue'
import TournamentStrip from '../components/TournamentStrip.vue'
import PwaInstallButton from '../components/PwaInstallButton.vue'
import DiscordJoinButton from '../components/DiscordJoinButton.vue'
import SupportButton from '../components/SupportButton.vue'

const ART = {
  vsAi: '/assets/home/home-vs-ai.webp',
  online: '/assets/home/home-online.webp',
  casual: '/assets/home/home-casual.webp',
  event: '/assets/home/home-event.webp',
  watch: '/assets/home/home-watch.webp',
  rank: '/assets/ranks/king.webp',
  rooms: '/assets/home/home-rooms.webp',
  packs: '/assets/home/home-packs.webp',
  missions: '/assets/ui/coin-token.webp',
  collection: '/assets/home/home-collection.webp',
  deck: '/assets/home/home-deck.webp',
} as const

const KEEP_ALL_PARAS = ['home.keepAllLead', 'home.keepAllRank'] as const

const NEXT_SET_PARAS = ['home.nextSetLead'] as const

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const collection = useCollectionStore()
const restoreCopies = ref(0)
const restoreDismissed = ref(false)
const showRestoreDialog = computed(
  () =>
    restoreCopies.value > 0 &&
    !restoreDismissed.value &&
    !collection.welcomePending &&
    route.query.needDeck !== '1',
)

onMounted(() => {
  void loadRestoreNotice()
})

async function loadRestoreNotice(): Promise<void> {
  if (!auth.token) return
  try {
    const body = await api<{ copies?: unknown }>('/account/card-restore', {
      token: auth.token,
      quiet: true,
    })
    const copies = body.copies
    if (typeof copies === 'number' && Number.isInteger(copies) && copies > 0) {
      restoreCopies.value = copies
    }
  } catch {
    restoreCopies.value = 0
  }
}

function onRestoreClose(): void {
  if (restoreDismissed.value) return
  restoreDismissed.value = true
  const token = auth.token
  if (!token) return
  void api('/account/card-restore/dismiss', {
    method: 'POST',
    token,
    quiet: true,
  }).catch(() => {
    /* The next visit to home asks again. */
  })
}
const deckStore = useDeckStore()
const player = usePlayerStore()
const missions = useMissionsStore()
const hasPendingNews = useChangelogNotice().pending
const keepAllNotice = useHomeNoticeFold('keepAll')
const nextSetNotice = useHomeNoticeFold('spellRuler')
const keepAllFolded = keepAllNotice.folded
const nextSetFolded = nextSetNotice.folded
const keepAllToggle = keepAllNotice.toggle
const nextSetToggle = nextSetNotice.toggle
const newsLinkLabel = computed(() =>
  hasPendingNews.value
    ? `${t('nav.news')}. ${t('changelog.pending')}`
    : undefined,
)
const ranked = useRankedLobbyStore()
const site = useSiteStore()
const duelPrefs = useDuelPrefsStore()
const lastCall = useLastCallStore()
const nudgeCoins = RANKED_NUDGE_COINS
activateFirstPlayable()
const deckOptions = computed(() => listDeckPlayOptions())
const activeDeckId = computed(() => deckStore.activeId || '')

function onActiveDeckChange(event: Event): void {
  const id = (event.target as HTMLSelectElement).value
  const opt = deckOptions.value.find((row) => row.id === id)
  if (!opt?.playable) return
  deckStore.setActiveId(id)
}

const startAIDuel = () => {
  DuelSfx.prime()
  armDuelLandscape()
  router.push('/online-duel?mode=local')
}

const playOnline = () => {
  DuelSfx.prime()
  router.push('/lobby')
}

const playCasual = () => {
  if (ranked.closed) return
  DuelSfx.prime()
  armDuelLandscape()
  router.push({
    path: '/online-duel',
    query: { mode: 'casual', username: player.username },
  })
}

const playEvent = () => {
  if (!site.eventQueue) return
  DuelSfx.prime()
  armDuelLandscape()
  router.push({
    path: '/online-duel',
    query: { mode: 'event', username: player.username },
  })
}
</script>

<style scoped>
.home {
  min-height: calc(100dvh - var(--app-nav-offset, 64px));
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
  font-family: Roboto, system-ui, sans-serif;
}

.home-inner {
  width: min(904px, 100%);
  margin: 0 auto;
  padding: 32px 16px 48px;
}

.keep-all,
.next-set {
  margin: 0 0 24px;
  padding: 16px;
  border-radius: 12px;
  border-left: 4px solid var(--md-sys-color-primary);
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.keep-all.folded,
.next-set.folded {
  padding: 12px 16px;
}

.next-set {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.next-set.folded .next-set-copy {
  width: 100%;
}

.notice-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.notice-head-copy {
  min-width: 0;
  flex: 1;
}

.notice-toggle {
  flex-shrink: 0;
  margin: 0;
  padding: 6px 12px;
  border: 0;
  border-radius: 999px;
  background: var(--md-sys-color-secondary-container);
  color: var(--md-sys-color-on-secondary-container);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  cursor: pointer;
  white-space: nowrap;
}

.notice-toggle:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

.notice-body {
  margin-top: 8px;
}

.keep-all h2,
.next-set h2 {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  color: var(--md-sys-color-primary);
}

.keep-all .kicker,
.next-set .kicker {
  margin: 0 0 4px;
}

.keep-all .em,
.next-set-copy .em {
  font-weight: 700;
  color: var(--md-sys-color-primary);
}

.keep-all .wipe,
.next-set-copy .wipe {
  font-weight: 700;
  color: var(--md-sys-color-error);
}

.keep-all p:not(.kicker),
.next-set-copy p:not(.kicker) {
  margin: 0 0 8px;
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
  line-height: 20px;
}

.keep-all .notice-body p:last-child,
.next-set-copy .notice-body p:last-child {
  margin-bottom: 0;
}

.next-set-art {
  width: 112px;
  height: auto;
  flex-shrink: 0;
  border-radius: 8px;
  box-shadow: var(--md-elevation-1);
}

.next-set-copy {
  min-width: 0;
  flex: 1;
}

.last-call {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin: 0 0 24px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.last-call p {
  margin: 0;
}

.hero {
  margin-bottom: 24px;
}

.hero :deep(.pwa-install) {
  margin-top: 12px;
}

.hero-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.hero-links :deep(.discord-btn),
.hero-links :deep(.support-btn) {
  display: flex;
  width: max-content;
  margin-top: 0;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 8px;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--md-sys-color-on-surface);
}

.session {
  margin: 0 0 16px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.chip {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.1px;
}

.chip.selected {
  border-color: transparent;
  background: var(--md-sys-color-primary-container);
  color: var(--md-sys-color-on-primary-container);
}

.deck-pick {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.deck-pick-label {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  font-weight: 500;
}

.deck-select {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
}

.menu {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.md-card {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  box-shadow: var(--md-elevation-1);
  color: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  font: inherit;
  transition: box-shadow 200ms cubic-bezier(0.2, 0, 0, 1);
}

.md-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--md-sys-color-on-surface);
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms cubic-bezier(0.2, 0, 0, 1);
}

.md-card:hover,
.md-card:focus-visible {
  box-shadow: var(--md-elevation-2);
  outline: none;
}

.md-card:hover::after {
  opacity: 0.08;
}

.md-card:focus-visible::after {
  opacity: 0.12;
}

.home-notice {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  pointer-events: none;
}

.media {
  display: block;
  height: 140px;
  overflow: hidden;
  background: var(--md-sys-color-surface-container);
}

.media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
}

.md-card.event-feature {
  grid-column: 1 / -1;
}

.event-feature .media {
  width: 100%;
  height: 200px;
}

.event-feature .media img {
  object-position: center 42%;
}

.event-feature .content {
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 8px;
}

.event-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.event-queue {
  flex-shrink: 0;
  padding-bottom: 2px;
}

.event-play {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  font: inherit;
}

.event-play:focus-visible {
  outline: none;
}

.event-standings {
  display: block;
  padding: 0 16px 16px;
  color: var(--md-sys-color-primary);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  letter-spacing: 0.25px;
  text-decoration: none;
}

.event-standings:hover,
.event-standings:focus-visible {
  text-decoration: underline;
  outline: none;
}

.md-card.event-feature:focus-within {
  box-shadow: var(--md-elevation-2);
}

.media-coin {
  display: grid;
  place-items: center;
  background:
    radial-gradient(circle at 50% 40%,
      color-mix(in srgb, var(--md-sys-color-primary) 28%, transparent),
      var(--md-sys-color-surface-container));
}

.media-coin img {
  width: 72px;
  height: 72px;
  object-fit: contain;
}

.rank-art {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: var(--md-elevation-1);
}

.media-rank .rank-art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.22);
}

.content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
}

.label-sm {
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.title {
  color: var(--md-sys-color-on-surface);
  font-size: 22px;
  line-height: 28px;
  font-weight: 400;
}

.news-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.news-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--md-sys-color-primary);
  flex-shrink: 0;
}

.support {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.queue-count,
.offer-line {
  font-weight: 700;
}

.queue-count {
  color: var(--md-sys-color-on-surface);
}

.offer-line {
  color: var(--md-sys-color-primary);
}

.md-card.news {
  grid-column: 1 / -1;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  min-height: 80px;
  padding-right: 8px;
}

.news .content {
  padding: 16px 8px 16px 16px;
}

.trailing {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  color: var(--md-sys-color-primary);
  font-size: 28px;
  line-height: 1;
}

@media (max-width: 720px) {
  h1 {
    font-size: 28px;
    line-height: 36px;
  }

  .keep-all,
  .next-set {
    gap: 12px;
    padding: 12px;
  }

  .next-set-art {
    width: 88px;
  }

  .keep-all h2,
  .next-set h2 {
    font-size: 20px;
    line-height: 26px;
  }

  .menu {
    grid-template-columns: 1fr;
  }

  .event-feature .media {
    height: 160px;
  }
}

/* Landscape phone / short window — keep two columns, flatten cards. */
@media (max-height: 500px) and (max-width: 1100px) {
  .home-inner {
    padding: 12px 16px 28px;
  }

  .keep-all,
  .next-set {
    margin-bottom: 12px;
    padding: 10px 12px;
    gap: 10px;
  }

  .next-set-art {
    width: 64px;
  }

  .keep-all h2,
  .next-set h2 {
    margin-bottom: 4px;
    font-size: 16px;
    line-height: 22px;
  }

  .keep-all p:not(.kicker),
  .next-set-copy p:not(.kicker) {
    font-size: 12px;
    line-height: 16px;
  }

  .hero {
    margin-bottom: 12px;
  }

  h1 {
    font-size: 22px;
    line-height: 28px;
    margin-bottom: 8px;
  }

  .menu {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .md-card:not(.news):not(.event-feature) {
    flex-direction: row;
    align-items: stretch;
    min-height: 72px;
  }

  .event-feature .media {
    width: 100%;
    height: 96px;
  }

  .event-feature .content {
    flex-direction: row;
    align-items: center;
    padding: 10px 12px 6px;
  }

  .event-standings {
    padding: 0 12px 10px;
    font-size: 12px;
    line-height: 16px;
  }

  .media {
    width: 96px;
    height: auto;
    min-height: 72px;
    flex-shrink: 0;
  }

  .content {
    padding: 10px 12px;
    justify-content: center;
  }

  .title {
    font-size: 16px;
    line-height: 22px;
  }

  .support {
    font-size: 12px;
    line-height: 16px;
  }

  .md-card.news {
    min-height: 0;
  }
}
</style>
